import { DrawCabinet } from "$src/CGS/domain/dto/article/Cabinet/DrawCabinet";
import { DrawCabinetShape } from "$src/CGS/domain/dto/article/shape/CabinetShapes/DrawCabinetShape";
import LengthValue from "$src/CGS/domain/dto/core/Length/LengthValue";
import { Euler, MathUtils, Matrix4, Quaternion, ShapeUtils, Vector2, Vector3 } from "three";
import { BaseFrontElementGenerator } from "../FrontElementGenerators/BaseFrontElementGenerator";
import { FrontFaceMetadata } from "../Metadata/FrontFaceMetadata";
import { InsideContainerMetadata } from "../Metadata/InsideContainerMetadata";
import { CabinetObject } from "../Objects/CabinetObject";
import { PlacementObject } from "../Objects/PlacementObject";
import { CabinetShapeGeneratorParameters } from "./CabinetShapeGeneratorParameters";
import { ICabinetShapeGenerator } from "./ICabinetShapeGenerator";
import { ICabinetShapeGeneratorParameters } from "./ICabinetShapeGeneratorParameters";
import { BoxObject } from "../Objects/BoxObject";
import { SubMeshCollection } from "../../Meshes/SubMeshCollection";
import { CabinetBoxFace } from "./CabinetBoxFace";
import { EFaceElements } from "../../Meshes/EFaceElements";
import { EMaterialSlot } from "../../Meshes/EMaterialSlot";
import { HitUtils } from "../../utils/HitUtils";
import { SwizzleExtension } from "../../utils/SwizzleExtension";
import { LengthValueMillimeter } from "$src/CGS/domain/dto/core/Length/LengthValueMillimeter";
import { GrooveObjectHelper } from "./HorizontalGrooveGenerator";
import { SubMesh } from "../../Meshes/SubMesh";
import { LengthMaths } from "$src/CGS/domain/dto/core/Length/LengthMaths";
import { TabUtils } from "../../utils/TabUtils";
import { VerticalSplit } from "./VerticalSplit";
import { EFrontElement } from "$src/CGS/domain/dto/core/EFrontElement";
import { FrontElementMetadata } from "../Metadata/FrontMetadata";
import { FrontElementGeneratorFactory } from "../FrontElementGenerators/FrontElementGeneratorFactory";
import { FrontFaceObject } from "../Objects/FrontFaceObject";
import { FrontFaceMetadataBuilder } from "../Metadata/Builder/FrontFaceMetadataBuilder";
import { LengthValueMeter } from "$src/CGS/domain/dto/core/Length/LengthValueMeter";
import { InsideContainerGenerator } from "../InsideElementGenerators/InsideContainerGenerator";
import { PlinthGenerator } from "./PlinthGenerator";


export abstract class BaseCabinetShapeGenerator<TData extends DrawCabinetShape> implements ICabinetShapeGenerator {
    // Data corresponding to specific shape
    public readonly data: TData;
    public readonly cabinet: DrawCabinet;

    public Parameters: ICabinetShapeGeneratorParameters;
    protected plinthGenerator: PlinthGenerator;
    protected boxThickness: LengthValue;
    protected doorThickness: LengthValue;
    protected height: LengthValue;
    public grooveDepth: LengthValue;
    public grooveWidth: LengthValue;
    protected horizontalGrooveHeight?: LengthValue;
    public hasLeftVerticalGroove: boolean = false;
    public hasRightVerticalGroove: boolean = false;
    public frontFaceMetadatas: FrontFaceMetadata[] = [];
    public insideContainerMetadatas: InsideContainerMetadata[] = [];
    public verticalSplits: VerticalSplit[] = [];

    constructor(cabinet: DrawCabinet, parameters: ICabinetShapeGeneratorParameters | null) {
        if (!(cabinet.CabinetShape instanceof DrawCabinetShape)) {
            throw new Error(`Cabinet shape is not a the same type as generator`);
        }

        this.data = cabinet.CabinetShape as TData;
        this.cabinet = cabinet;
        this.Parameters = parameters || new CabinetShapeGeneratorParameters();

        this.height = LengthMaths.getLengthValue(this.data.Height);
        this.boxThickness = cabinet.Constants.BoxThickness;
        this.doorThickness = cabinet.Constants.DoorThickness || BaseFrontElementGenerator.DOOR_THICKNESS;

        this.grooveDepth = new LengthValueMillimeter(15);
        this.grooveWidth = this.cabinet.Constants.GrooveWidth;
        
        this.verticalSplits = this.getVerticalSplits();

        this.frontFaceMetadatas = new FrontFaceMetadataBuilder(this).Build(cabinet);
        this.insideContainerMetadatas = this.frontFaceMetadatas
            .flatMap(f => f.FrontElements)
            .map(e => e.Inside)
            .filter(i => i !== null && i !== undefined) as InsideContainerMetadata[];

        this.plinthGenerator = new PlinthGenerator(this);
    }
  
    private getVerticalSplits(): VerticalSplit[] {
        const horizontalGrooves = this.frontFaceMetadatas
            .flatMap(front => front.FrontElements.filter(element => element instanceof FrontElementMetadata && element.Type === EFrontElement.G) as FrontElementMetadata[])
            .filter(element => element.Anchor.y < this.height.meter)
            .sort((a, b) => a.Anchor.y - b.Anchor.y)
            .map(element => ({
                bottom: element.Anchor.y - 0.016,
                top: Math.min(this.height.meter, element.Anchor.y + LengthMaths.getLengthValue(element.Height).meter + 0.016),
                element: element
            }))
            .filter(element => element.bottom < this.height.meter);
    
        // split vertically the face for adjacent groove slices, from bottom to top
        let splits: VerticalSplit[];
    
        if (horizontalGrooves.length > 0) {
            // for each horizontal groove, create a split
            splits = horizontalGrooves.map(grooveHeight => new VerticalSplit(grooveHeight.top, grooveHeight.bottom, grooveHeight.element, true));
    
            // Add non-groove splits before each groove split (fill the non-groove space)
            for (let j = 0; j < splits.length; ++j) {
                if (splits[j].IsGroove && splits[j].Bottom !== 0.0) {
                    const top = j === 0 ? 0 : splits[j - 1].Top;
                    splits.splice(j, 0, new VerticalSplit(splits[j].Bottom, top, null, false));
                    ++j;
                }
            }
    
            // add last split if the last groove doesn't reach the top
            if (splits[splits.length - 1].Top < this.height.meter - 0.001) {
                // Check if the groove is the last front element. If so, recess the last split as well (Consider it as a groove)
                const recessLastSplit = GrooveObjectHelper.isTopGrooveV1(this.frontFaceMetadatas, splits[splits.length - 1].FrontElement!);
    
                splits.push(new VerticalSplit(this.height.meter, splits[splits.length - 1].Top, null, recessLastSplit));
            }
        } else {
            // single split, from bottom to top
            splits = [new VerticalSplit(this.height.meter, 0, null, false)];
        }
    
        return splits;
    }
    
    protected get BackDistance(): number {
        return LengthMaths.getLengthValue(this.cabinet.CabinetShape.Depth).millimeter > 500 ? 0.2825 : 0.2250;
    }

    protected abstract getAnchorOffset(): Vector3;

    BuildCabinet(): CabinetObject {
        
        const cabinet = new CabinetObject(this.cabinet.ProviderRef);

        cabinet.Placement = new PlacementObject();
        cabinet.Placement.position.set(
            -this.getAnchorOffset().x, 
            -this.getAnchorOffset().y, 
            -this.getAnchorOffset().z
        );
        cabinet.add(cabinet.Placement);

        cabinet.Box = this.BuildBox(cabinet.Placement);
        this.BuildFrontFace(cabinet);
        this.BuildInsideContainers(cabinet);
        this.BuildPlinth(cabinet);

        return cabinet;
    }

    BuildBox(parent: PlacementObject) : BoxObject {

        const box = new BoxObject();

        const subMeshCollection = new SubMeshCollection();
        const faceDescriptions = this.getProcessedFaceDescription();
        const outline = this.getOutline();

        const insideOutline = this.getInsideOutline();
        const insideHoleOutline = this.getInsideOutline(LengthMaths.add(this.boxThickness, this.grooveDepth));
        const insidePillarOutline = this.getInsideOutline(this.boxThickness);
    
         // Top outline should be clockwise as seen from the top, but start at the same vertex (for winding)
        
        const topOutline = TabUtils.roll([...outline].reverse(), -1).map(v => new Vector3(v.x, this.height.meter, v.z));


        // Same for the inside top outline
        const insideTopOutline = TabUtils.roll([...insideOutline].reverse(), -1).map(v => new Vector3(v.x, this.height.meter, v.z));

        const topPlankInsideOutline = GrooveObjectHelper.hasTopGroove(this.frontFaceMetadatas) ?  this.getInsideOutline(this.grooveDepth) : insideOutline;

        // build top plank
        subMeshCollection.merge(this.buildTopPlank(topPlankInsideOutline, insideHoleOutline, faceDescriptions));

        // build bottom plank
        subMeshCollection.merge(this.buildBottomPlank(insideOutline, faceDescriptions));

        // // build side planks
        subMeshCollection.merge(this.buildSidePlanks(outline, insideOutline, faceDescriptions));

        // build pillars
        subMeshCollection.merge(this.buildPillars(outline, insidePillarOutline, faceDescriptions));

        // // front outline
        // subMeshCollection.Merge(this.buildFrontOutline(outline, insideOutline, faceDescriptions));

        box.MeshInfo = subMeshCollection.createMesh(box);

        parent.add(box);

        return box;
    }
    
    BuildPlinth(cabinet: CabinetObject) {
        const plinth = this.plinthGenerator.generate(cabinet);
        
        if (plinth !== null) {
            // If plinth was generated, move the placement bone
            const placement = cabinet.Placement;
            placement.position.set(
                placement.position.x,
                this.cabinet.Constants.PlinthHeight.meter,
                placement.position.z
            );
        }
    }

    private BuildFrontFace(cabinet: CabinetObject): void {
        if (!this.cabinet.Fronts || this.cabinet.Fronts.length === 0) return;
    
        for (const frontFaceMetadata of this.frontFaceMetadatas) {
            
            const frontFace = new FrontFaceObject();
            frontFace.name = `FRONT_FACE${frontFaceMetadata.Index}_GRP`;
    
            cabinet.FrontFaces.push(frontFace);
            cabinet.Placement?.add(frontFace);
            
            let pos = this.frontFacePosition(frontFaceMetadata.Index);
            frontFace.position.set(pos.x, pos.y, pos.z);
            frontFace.rotation.setFromQuaternion(this.frontFaceOrientation(frontFaceMetadata.Index));
            
            for (const frontElementMetadata of frontFaceMetadata.FrontElements.filter(metadata => metadata instanceof FrontElementMetadata)) {
                const frontElementGenerator = FrontElementGeneratorFactory.getGenerator(frontElementMetadata as FrontElementMetadata, this.doorThickness, this.Parameters, this.cabinet);
    
                if (!frontElementGenerator) continue;
    
                const frontElement = frontElementGenerator.BuildFrontElement(frontFace);
                frontFace.add(frontElement);
                
                let pos = new Vector3(frontElementMetadata.Anchor.x, frontElementMetadata.Anchor.y, frontElementMetadata.Anchor.z);
                frontElement.position.set(pos.x, pos.y, pos.z);;
                frontFace.FrontElements.push(frontElement);
            }
        }
    }

    public BuildInsideContainers(cabinet: CabinetObject): void {
        this.insideContainerMetadatas.forEach(insideContainerMetadata => {
            const insideContainerGenerator = new InsideContainerGenerator(cabinet, insideContainerMetadata, this);
            insideContainerGenerator.build();
        });
    }

    public abstract getOutline(): Vector3[]
    public abstract getDefaultFaceDescription(): CabinetBoxFace[];

    public getInsideOutline(frontEdgeThickess: LengthValue | null = null, boxThickness: LengthValue | null = null): Vector3[] {
        const faces: CabinetBoxFace[] = this.getProcessedFaceDescription();
        const outline: Vector3[] = this.getOutline();
    
        if (frontEdgeThickess === null)
            frontEdgeThickess = LengthMaths.Zero;
    
        const cutFrontEdges: boolean = frontEdgeThickess === LengthMaths.Zero;
    
        if (boxThickness === null)
            boxThickness = this.boxThickness;
    
        const insideOutline: Vector3[] = faces.map((_, i) => {
            const prevFace = faces[(faces.length + i - 1) % faces.length];
            const face = faces[i];
    
            const previous = outline[(outline.length + i - 1) % outline.length];
            const current = outline[i];
            const next = outline[(i + 1) % outline.length];
    
            const n1 = new Vector3().crossVectors(current.clone().sub(previous).normalize(), new Vector3(0, 1, 0));
            const n2 = new Vector3().crossVectors(next.clone().sub(current).normalize(), new Vector3(0, 1, 0));
    
            if (cutFrontEdges && prevFace.material === EMaterialSlot.Front && face.material === EMaterialSlot.Front)
                return current.clone();
    
            const d1 = prevFace.material === EMaterialSlot.Front
                ? frontEdgeThickess.meter
                : boxThickness.meter;
    
            const d2 = face.material === EMaterialSlot.Front
                ? frontEdgeThickess.meter
                : boxThickness.meter;
    
                
            if ((n1.clone().sub(n2)).lengthSq() < 0.001)
                return current.clone().add(n1.clone().multiplyScalar(d2));

            const v1 = current.clone().add(n1.clone().multiplyScalar(d1));
            const v2 = previous.clone().add(n1.clone().multiplyScalar(d1));
            const v3 = current.clone().add(n2.clone().multiplyScalar(d2));
            const v4 = next.clone().add(n2.clone().multiplyScalar(d2));
    
            const intersect = new Vector2();
            
            if (HitUtils.LinesIntersectWithPointExtended(SwizzleExtension.XZ(v1), SwizzleExtension.XZ(v2), SwizzleExtension.XZ(v3), SwizzleExtension.XZ(v4), intersect))
                return new Vector3(intersect.x, current.y, intersect.y);
            else
                return current.clone();
        });
    
        return insideOutline;
    }
    

    public getProcessedFaceDescription(): CabinetBoxFace[] {
        let faceDescriptions = this.getDefaultFaceDescription();
        const faceCount = faceDescriptions.filter(x => x.faceIndex >= 0).length;
        const fronts = this.cabinet.Fronts;
    
        for (const front of fronts) {
            for (const frontFace of front.Faces) {
                const face = faceDescriptions.find(f => f.faceIndex === frontFace.Index);
                if (face) {
                    face.elements &= ~EFaceElements.Fill;
                    face.material = EMaterialSlot.Front;
                }
            }
    
            let frontPairs = front.Faces.map((val, idx) => [val, front.Faces[(idx + 1) % front.Faces.length]]);
            if (front.Faces.length >= 2)
                frontPairs.push([front.Faces[front.Faces.length - 1], front.Faces[0]]);
    
            for (const [f1Id, f2Id] of frontPairs) {
                if (f2Id.Index === (f1Id.Index + 1) % faceCount) {
                    const f1 = faceDescriptions.find(f => f.faceIndex === f1Id.Index);
                    const f2 = faceDescriptions.find(f => f.faceIndex === f2Id.Index);
                    if (f1) f1.elements &= ~EFaceElements.RightPillar;
                    if (f2) f2.elements &= ~EFaceElements.LeftPillar;
                }
            }
        }
    
        return faceDescriptions;
    }
    

    protected buildTopPlank(insideOutline: Vector3[], insideFullOutline: Vector3[], faces: CabinetBoxFace[]): SubMeshCollection {
        const collection = new SubMeshCollection();

        if (this.cabinet.IsTopOpen) {
            // create the polygon insideOutline with insideFullOutline as a hole inside it
            const vertices = [...insideOutline, ...insideFullOutline].map(v => v.clone().add(new Vector3(0, this.height?.meter, 0)));

            // create a quad between 2 points of insideOutline and the corresponding 2 points in insideFullOutline
            const triangles: number[] = [];
            for (let i = 0; i < insideOutline.length; i++) {
                const next = (i + 1) % insideOutline.length;
                triangles.push(
                    i, next, next + insideOutline.length,
                    next + insideOutline.length, i + insideOutline.length, i
                );
            }

            const topFace = new SubMesh(vertices, triangles).flip();
            const bottomFace = new SubMesh(vertices.map(v => v.clone().sub(new Vector3(0, this.boxThickness.meter, 0))), triangles);

            collection.add(topFace, EMaterialSlot.Box);
            collection.add(bottomFace, EMaterialSlot.Inside);

            // All edges of the insideFullOutline are extruded to the bottom, with a face towards the inside
            for (let i = 0; i < insideFullOutline.length; i++) {
                const v1 = insideFullOutline[i];
                const v2 = insideFullOutline[(i + 1) % insideFullOutline.length];
                collection.add(SubMesh.polygon(
                    v2.clone().add(new Vector3(0, LengthMaths.subtract(this.height, this.boxThickness).meter, 0)),
                    v2.clone().add(new Vector3(0, this.height.meter, 0)),
                    v1.clone().add(new Vector3(0, this.height.meter, 0)),
                    v1.clone().add(new Vector3(0, LengthMaths.subtract(this.height, this.boxThickness).meter, 0))
                ), EMaterialSlot.Inside);
            }
        } else {

            const shapePoints = insideOutline.map(v => new Vector2(v.x, v.z));

            const triangles = ShapeUtils.triangulateShape(shapePoints, []);

            const indices: number[] = [];
            triangles.forEach(i => {
                indices.push(i[0], i[1], i[2]);
            });
                        
            const topVertices = shapePoints.map(v => new Vector3(v.x, this.height?.meter, v.y));
            const bottomVertices = shapePoints.map(v => new Vector3(v.x, LengthMaths.subtract(this.height, this.boxThickness).meter, v.y));

            collection.add(new SubMesh(topVertices, indices).flip(), EMaterialSlot.Box);
            collection.add(new SubMesh(bottomVertices, indices), EMaterialSlot.Inside);
        }

        // All edges of the insideOutline are extruded to the bottom, with a face towards the outside
        for (let i = 0; i < insideOutline.length; i++) {
            const v1 = insideOutline[i];
            const v2 = insideOutline[(i + 1) % insideOutline.length];
            collection.add(SubMesh.polygon(
                v1.clone().add(new Vector3(0, LengthMaths.subtract(this.height, this.boxThickness).meter, 0)),
                v1.clone().add(new Vector3(0, this.height.meter, 0)),
                v2.clone().add(new Vector3(0, this.height.meter, 0)),
                v2.clone().add(new Vector3(0,  LengthMaths.subtract(this.height, this.boxThickness).meter, 0))
            ), EMaterialSlot.Front);
        }

        return collection;
    }

    protected buildBottomPlank(outline: Vector3[], faces: CabinetBoxFace[]): SubMeshCollection {

        const collection = new SubMeshCollection();

        const shapePoints = outline.map(v => new Vector2(v.x, v.z));
        const triangles = ShapeUtils.triangulateShape(shapePoints, []);

        const indices: number[] = [];
        triangles.forEach(i => {
            indices.push(i[0], i[1], i[2]);
        });
                    
        const vertices1 = shapePoints.map(v => new Vector3(v.x, 0, v.y));
        const vertices2 = shapePoints.map(v => new Vector3(v.x, this.boxThickness.meter, v.y));
        collection.add(new SubMesh(vertices1, indices), EMaterialSlot.Box);
        collection.add(new SubMesh(vertices2, indices).flip(), EMaterialSlot.Inside);

        // All edges are drawn with the Front material.
        // They are behind the side planks, so they only appear when a front face is visible
        outline.forEach((v1, index) => {
            const v2 = outline[(index + 1) % outline.length];
            const frontVertices = [
                new Vector3(v1.x, 0, v1.z),
                new Vector3(v1.x, this.boxThickness.meter, v1.z),
                new Vector3(v2.x, this.boxThickness.meter, v2.z),
                new Vector3(v2.x, 0, v2.z)
            ];
            collection.add(SubMesh.polygon(...frontVertices), EMaterialSlot.Front);
        });

        return collection;
    }

    protected buildSidePlanks(outline: Vector3[], insideOutline: Vector3[], faceDescriptions: CabinetBoxFace[]) {
        const subMeshCollection = new SubMeshCollection();

        for (let i = 0; i < outline.length; ++i) {

            // Ne pas dessiner les planches latérales pour les faces frontales
            if (faceDescriptions[i].material === EMaterialSlot.Front)
                continue;

             // Face et faces adjacentes
            const prevFace = faceDescriptions[(faceDescriptions.length + i - 1) % faceDescriptions.length];
            const face = faceDescriptions[i];
            const nextFace = faceDescriptions[(i + 1) % faceDescriptions.length];

            // Contour et contours adjacents
            const previousOutline = outline[(outline.length + i - 1) % outline.length];
            const currentOutline = outline[i];
            const nextOutline = outline[(i + 1) % outline.length];
            const nextNextOutline = outline[(i + 2) % outline.length];

            // Contour intérieur et contours adjacents intérieurs
            const previousInsideOutline = insideOutline[(insideOutline.length + i - 1) % insideOutline.length];
            const currentInsideOutline = insideOutline[i];
            const nextInsideOutline = insideOutline[(i + 1) % insideOutline.length];

            const tangent = (nextOutline.clone().sub(currentOutline)).normalize();
            const nextTangent = (nextNextOutline.clone().sub(nextOutline)).normalize();
            const prevTangent = (currentOutline.clone().sub(previousOutline)).normalize();

            const isParallelToNext = tangent.dot(nextTangent) > 0.99;
            const isParallelToPrev = tangent.dot(prevTangent) > 0.99;

            // Normales orientées vers l'intérieur
            const normal = new Vector3().crossVectors(tangent, new Vector3(0, 1, 0));

             // Vérifications de rainure adjacente
            const hasGrooveToLeft = prevFace.material === EMaterialSlot.Front
            && face.material !== EMaterialSlot.Front
            && !isParallelToPrev
            && (this.hasRightVerticalGroove || this.verticalSplits.some(s => s.IsGroove));

            const hasGrooveToRight = nextFace.material === EMaterialSlot.Front
            && face.material !== EMaterialSlot.Front
            && !isParallelToNext
            && (this.hasLeftVerticalGroove || this.verticalSplits.some(s => s.IsGroove));


             // Supprimer les séparations s'il n'y a pas de rainure adjacente
            const faceSplits = hasGrooveToLeft || hasGrooveToRight
            ? this.verticalSplits
            : [new VerticalSplit(this.height.meter, 0, null, false)];


               // Dessiner chaque séparation
            faceSplits.forEach(split => {
                // Sommet et base de la séparation (du bas du meuble)
                const toTop = new Vector3(0, split.Top, 0);
                const toBot = new Vector3(0, split.Bottom, 0);

                const splitHasGrooveToLeft = hasGrooveToLeft && (split.IsGroove || this.hasRightVerticalGroove);
                const splitHasGrooveToRight = hasGrooveToRight && (split.IsGroove || this.hasLeftVerticalGroove);

                // Calcul du décalage du côté pour laisser de la place pour la rainure
                const leftAngle =  MathUtils.radToDeg(tangent.angleTo(new Vector3().sub(prevTangent))) - 90;
                const leftOffsetDist = Math.sqrt(1 + Math.pow(Math.tan(MathUtils.degToRad(leftAngle)), 2)) * this.grooveDepth.meter;

                const rightAngle =  MathUtils.radToDeg(new Vector3().sub(tangent).angleTo(nextTangent)) - 90;
                const rightOffsetDist = Math.sqrt(1 + Math.pow(Math.tan(MathUtils.degToRad(rightAngle * Math.PI / 180)), 2)) * this.grooveDepth.meter;

                // Décalages à gauche et à droite, au cas où il y aurait une rainure adjacente à cette séparation
                const leftOffset = tangent.clone().multiplyScalar(splitHasGrooveToLeft ? leftOffsetDist : 0);
                const rightOffset = tangent.clone().multiplyScalar(splitHasGrooveToRight ? rightOffsetDist : 0);

                // Décalage du contour de la face
                const splitCurrentOutline = currentOutline.clone().add(leftOffset);
                const splitCurrentInsideOutline = currentInsideOutline.clone().add(leftOffset);

                const splitNextOutline = nextOutline.clone().add(rightOffset);
                const splitNextInsideOutline = nextInsideOutline.clone().add(rightOffset);

                // Face supérieure de la séparation
                subMeshCollection.add(SubMesh.polygon(
                    splitCurrentOutline.clone().add(toTop),
                    splitCurrentInsideOutline.clone().add(toTop),
                    splitNextInsideOutline.clone().add(toTop),
                    splitNextOutline.clone().add(toTop)
                ), EMaterialSlot.Box);

                // Face inférieure de la séparation
                subMeshCollection.add(SubMesh.polygon(
                    splitCurrentOutline.clone(),
                    splitNextOutline.clone(),
                    splitNextInsideOutline.clone(),
                    splitCurrentInsideOutline.clone()
                ), EMaterialSlot.Box);

                // Face extérieure
                subMeshCollection.add(SubMesh.polygon(
                    splitCurrentOutline.clone().add(toBot),
                    splitCurrentOutline.clone().add(toTop),
                    splitNextOutline.clone().add(toTop),
                    splitNextOutline.clone().add(toBot)
                ), face.material);

                // Face intérieure
                subMeshCollection.add(SubMesh.polygon(
                    splitNextInsideOutline.clone().add(toBot),
                    splitNextInsideOutline.clone().add(toTop),
                    splitCurrentInsideOutline.clone().add(toTop),
                    splitCurrentInsideOutline.clone().add(toBot)
                ), EMaterialSlot.Inside);

                // Face de bord gauche
                if (prevFace.material === EMaterialSlot.Front && !splitCurrentOutline.equals(splitCurrentInsideOutline)) {
                    subMeshCollection.add(SubMesh.polygon(
                        splitCurrentInsideOutline.clone().add(toBot),
                        splitCurrentInsideOutline.clone().add(toTop),
                        splitCurrentOutline.clone().add(toTop),
                        splitCurrentOutline.clone().add(toBot)
                    ), EMaterialSlot.Front);
                }

                // Face de bord droit
                if (nextFace.material === EMaterialSlot.Front && !splitNextOutline.equals(splitNextInsideOutline)) {
                    subMeshCollection.add(SubMesh.polygon(
                        splitNextOutline.clone().add(toBot),
                        splitNextOutline.clone().add(toTop),
                        splitNextInsideOutline.clone().add(toTop),
                        splitNextInsideOutline.clone().add(toBot)
                    ), EMaterialSlot.Front);
                }
            });
        }

        return subMeshCollection;
    }

    /// <summary>
    /// Build pillars between all consecutive front faces with a pillar inbetween
    /// </summary>
    /// <param name="outline">Furniture outline</param>
    /// <param name="insideOutline">Furniture inside outline (with front thickness = BoxThickness)</param>
    /// <param name="faceDescriptions">Face descriptions</param>
    /// <returns></returns>
    protected buildPillars(outline: Vector3[], insideOutline: Vector3[], faceDescriptions: CabinetBoxFace[]): SubMeshCollection {
        const collection = new SubMeshCollection();

        // Skip pillars if there are vertical grooves
        if (this.hasLeftVerticalGroove || this.hasRightVerticalGroove) {
            return collection;
        }

        for (let i = 0; i < faceDescriptions.length; ++i) {
            const face = faceDescriptions[i];
            const prevFace = faceDescriptions[(faceDescriptions.length + i - 1) % faceDescriptions.length];

            if (face.material !== EMaterialSlot.Front || prevFace.material !== EMaterialSlot.Front
                || !(face.elements & EFaceElements.LeftPillar) || !(prevFace.elements & EFaceElements.RightPillar)) {
                continue;
            }

            const current = outline[i];
            const prev = outline[(outline.length + i - 1) % outline.length];
            const next = outline[(outline.length + i + 1) % outline.length];

            const tangent = new Vector3().subVectors(next, current).normalize();
            const prevTangent = new Vector3().subVectors(prev, current).normalize();

            const normal = new Vector3().crossVectors(tangent, new Vector3(0, 1, 0));
            const prevNormal = new Vector3().crossVectors(prevTangent, new Vector3(0, -1, 0));

            // pillar outline
            //
            //  3____C____2
            //  |         |
            //  |         |B
            //  |    _____|
            // D|   |0  A 1
            //  |   |
            //  |   |F
            //  |___|
            //  4 E 5

            const boxThicknessMeter = this.boxThickness.meter;

            const pillarOutline = [
                current.clone(),
                current.clone().add(tangent.clone().multiplyScalar(boxThicknessMeter)),
                current.clone().add(tangent.clone().multiplyScalar(boxThicknessMeter)).add(normal.clone().multiplyScalar(boxThicknessMeter)),
                current.clone().add(normal.clone().multiplyScalar(boxThicknessMeter)).add(prevNormal.clone().multiplyScalar(boxThicknessMeter)),
                current.clone().add(prevTangent.clone().multiplyScalar(boxThicknessMeter)).add(prevNormal.clone().multiplyScalar(boxThicknessMeter)),
                current.clone().add(prevTangent.clone().multiplyScalar(boxThicknessMeter)),
            ];

            for (const split of this.verticalSplits) {
                // do not draw pillar where there is a horizontal groove height
                if (split.IsGroove) continue;

                const toTop = new Vector3(0, Math.min(split.Top, this.height.meter - this.boxThickness.meter), 0);
                const toBot = new Vector3(0, Math.max(split.Bottom, this.boxThickness.meter), 0);

                if (toTop.y <= toBot.y) continue;

                // A
                collection.add(SubMesh.polygon(pillarOutline[0].clone().add(toBot), pillarOutline[0].clone().add(toTop), pillarOutline[1].clone().add(toTop), pillarOutline[1].clone().add(toBot)), EMaterialSlot.Front);
                // F
                collection.add(SubMesh.polygon(pillarOutline[5].clone().add(toBot), pillarOutline[5].clone().add(toTop), pillarOutline[0].clone().add(toTop), pillarOutline[0].clone().add(toBot)), EMaterialSlot.Front);
                // B
                collection.add(SubMesh.polygon(pillarOutline[1].clone().add(toBot), pillarOutline[1].clone().add(toTop), pillarOutline[2].clone().add(toTop), pillarOutline[2].clone().add(toBot)), EMaterialSlot.Inside);
                // C
                collection.add(SubMesh.polygon(pillarOutline[2].clone().add(toBot), pillarOutline[2].clone().add(toTop), pillarOutline[3].clone().add(toTop), pillarOutline[3].clone().add(toBot)), EMaterialSlot.Inside);
                // D
                collection.add(SubMesh.polygon(pillarOutline[3].clone().add(toBot), pillarOutline[3].clone().add(toTop), pillarOutline[4].clone().add(toTop), pillarOutline[4].clone().add(toBot)), EMaterialSlot.Inside);
                // E
                collection.add(SubMesh.polygon(pillarOutline[4].clone().add(toBot), pillarOutline[4].clone().add(toTop), pillarOutline[5].clone().add(toTop), pillarOutline[5].clone().add(toBot)), EMaterialSlot.Inside);
            }
        }

        return collection;
    }

    public getFrontFaceIndexInOutline(frontFaceIndex: number, findEndPosition: boolean = false): number {
        const defaultFaceDescription = this.getDefaultFaceDescription();

        if (findEndPosition) {
            frontFaceIndex = (frontFaceIndex + 1) % defaultFaceDescription.length; 
        }

        let faceIndexInOutlineArray = 0;
        const faceDescription = defaultFaceDescription.find(cbf => cbf.faceIndex === frontFaceIndex);
        if (faceDescription) {
            faceIndexInOutlineArray = defaultFaceDescription.indexOf(faceDescription);
        }

        return faceIndexInOutlineArray;
    }

    public frontFacePosition(frontFaceIndex: number, findEndPosition: boolean = false): Vector3 {
        return this.getOutline()[this.getFrontFaceIndexInOutline(frontFaceIndex, findEndPosition)];
    }
    
    protected frontFaceOrientation(frontFaceIndex: number): Quaternion {
        const startPosition: Vector3 = this.FrontFacePosition(frontFaceIndex);
        const endPosition: Vector3 = this.FrontFacePosition(frontFaceIndex, true);
    
        const direction: Vector3 = endPosition.clone().sub(startPosition).normalize();
        const rotationAxis: Vector3 = new Vector3(0, 1, 0); // Axe de rotation pour une rotation de 90 degrés autour de l'axe y
    
        // Calcul de la rotation pour faire face à la direction du vecteur
        const rotationMatrix: Matrix4 = new Matrix4().lookAt(startPosition, endPosition, rotationAxis);
        const lookRotation: Quaternion = new Quaternion().setFromRotationMatrix(rotationMatrix);
    
        // Rotation supplémentaire de -90 degrés autour de l'axe y
        const eulerRotation: Quaternion = new Quaternion().setFromEuler(new Euler(0, MathUtils.degToRad(90), 0));
    
        // Combinaison des rotations
        return lookRotation.multiply(eulerRotation);
    }

    public FrontFaceSize(frontFaceIndex: number): LengthValue {
        const startPosition: Vector3 = this.FrontFacePosition(frontFaceIndex);
        
        const endPosition: Vector3 = this.FrontFacePosition(frontFaceIndex, true);

        const magnitude: number = endPosition.clone().sub(startPosition).length();

        return new LengthValueMeter(magnitude);
    }
    
    GetFrontFaceIndexInOutline(frontFaceIndex: number, findEndPosition: boolean = false): number {
        const defaultFaceDescription: CabinetBoxFace[] = this.getDefaultFaceDescription();

        if (findEndPosition) {
            frontFaceIndex = (frontFaceIndex + 1) % defaultFaceDescription.length;
        }

        let faceIndexInOutlineArray: number = 0;
        const faceDescription = defaultFaceDescription.find(cbf => cbf.faceIndex === frontFaceIndex);
        if (faceDescription !== undefined) {
            faceIndexInOutlineArray = defaultFaceDescription.indexOf(faceDescription);
        }

        return faceIndexInOutlineArray;
    }

    FrontFacePosition(frontFaceIndex: number, findEndPosition: boolean = false): Vector3 {
        const outline: Vector3[] = this.getOutline();
        const indexInOutline: number = this.GetFrontFaceIndexInOutline(frontFaceIndex, findEndPosition);
        return outline[indexInOutline];
    }

    FrontFaceOrientation(frontFaceIndex: number): Quaternion {
        const startPosition: Vector3 = this.FrontFacePosition(frontFaceIndex);
        const endPosition: Vector3 = this.FrontFacePosition(frontFaceIndex, true);

        const direction: Vector3 = new Vector3().subVectors(endPosition, startPosition).normalize();
        const quaternion: Quaternion = new Quaternion().setFromUnitVectors(new Vector3(0, 0, 1), direction);

        return quaternion.multiply(new Quaternion().setFromEuler(new Euler(0, -Math.PI / 2, 0)));
    }
}
